import React from 'react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import Link from '@dashboard/components/Link';
import { Form } from '@dashboard/components/Formik';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { useToast } from '@dashboard/context/ToastContext';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';

const SettingsStripeTax = () => {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const { emitToast } = useToast();

  return (
    <SettingsContainer size="2xl">
      <SettingsHeader id="tax">Stripe Tax</SettingsHeader>
      <div>
        Enable{' '}
        <Link href="https://stripe.com/tax" target="_blank" external variant="secondary">
          Stripe Tax
        </Link>{' '}
        to automatically calculate, collect and remit VAT, Sales tax and/or GST on your sales.
      </div>

      <SettingsDivider />

      <span className="block mt-2">
        {seller?._id && (
          <Form
            autosave
            showState={false}
            initialValues={{
              tax: seller.tax,
            }}
            fields={[
              {
                name: 'tax.enabled',
                required: false,
                type: 'checkbox',
                label: 'Enable Stripe Tax',
              },
            ]}
            onSubmit={async values => {
              try {
                const { data } = await updateSeller(values);

                return {
                  data,
                  onSuccess: () => {
                    emitToast({
                      title: data?.tax?.enabled ? 'Stripe Tax enabled' : 'Stripe Tax disabled',
                    });
                  },
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
          />
        )}
      </span>

      <SettingsDivider />

      <SettingsHeader id="set-up">Set up Stripe Tax</SettingsHeader>

      <div className="mb-4">
        This is how you set up Stripe Tax with Checkout Page. For detailed instructions,{' '}
        <Link to="/help/articles/how-to-charge-tax" target="_blank" variant="secondary">
          read our help article
        </Link>
      </div>

      <div>
        <ul className="list-decimal list-inside space-y-4 mb-4">
          <li>Enable Stripe Tax with the checkbox above</li>
          <li>
            <Link href={getStripeDashboardUrl('/tax')} target="_blank" external variant="secondary">
              Enable Stripe Tax
            </Link>{' '}
            in your Stripe Dashboard
          </li>
          <li>
            <Link
              href={getStripeDashboardUrl('/settings/stripe-tax')}
              target="_blank"
              external
              variant="secondary"
            >
              Set up your tax settings
            </Link>{' '}
            in your Stripe Dashboard. You can set your default tax category and if tax is included
            or excluded in your product prices.
          </li>
          <li>
            <Link
              href={getStripeDashboardUrl('/tax/registrations')}
              target="_blank"
              external
              variant="secondary"
            >
              Add your tax registrations
            </Link>{' '}
            in the Stripe Dashboard. Tax registrations are locations where you’re registered to
            collect and remit taxes.
          </li>
          <li>
            You must add the following fields (and corresponding data types) to your pages to
            calculate and collect taxes:
            <ul className="list-disc list-inside ml-5 mt-2 space-y-1">
              <li>
                <strong>United States</strong>: Country, address line one, postal code
              </li>
              <li>
                <strong>Canada</strong>: Country, postal code
              </li>
              <li>
                <strong>EU</strong>: Country, postal code
              </li>
              <li>
                <strong>United Kingdom</strong>: Country
              </li>
              <li>
                <strong>All other countries</strong>: Country
              </li>
            </ul>
          </li>
          <li>
            If you’re selling to businesses, you’ll likely want to collect their Tax ID (e.g. VAT
            number). To do so, you can add a Tax ID field to your checkout.
          </li>
        </ul>

        <p className="mb-4">
          The tax will be (re)calculated as your customer moves through your checkout form, based on
          the checkout price, customer location and customer Tax ID.
        </p>

        <p>
          Stripe will charge the taxable amount on all payments, display the tax on your customer
          invoices and provide you with exports of your transactions.
        </p>
      </div>

      <SettingsDivider />

      <SettingsHeader id="per-checkout">Per-page settings</SettingsHeader>
      <ol className="list-decimal list-inside space-y-4">
        <li>
          You can enable or disable Stripe Tax for individual pages in the page's checkout settings.
          Edit your page, click on the "Checkout" tab, and look for "Stripe Tax".
        </li>
        <li>
          You can set the tax behavior (inclusive or exclusive) for individual pages in the page's
          checkout settings.
          <br />
          <br />
          Subscription pages always use the tax behavior set in your{' '}
          <Link to={getStripeDashboardUrl('/settings/tax')} target="_blank" external>
            Stripe Tax settings
          </Link>
          .
        </li>
      </ol>

      <SettingsDivider />
    </SettingsContainer>
  );
};

export default SettingsStripeTax;
